import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@edupra/shell';
import { trpc, createTRPCClient } from '@/lib/platform-trpc';
import { checkAuth, useAuth } from '@/lib/platform-auth';
import { ProtectedRoute } from '@/components/protected-route';
import { AdminLayout } from '@/layouts/admin-layout';
import { LoginPage } from '@/pages/login';
import { OverviewPage } from '@/pages/overview';
import { PlaceholderPage } from '@/pages/placeholder';

export function App() {
  const [trpcClient] = useState(() => createTRPCClient());
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
        },
      }),
    [],
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    checkAuth().finally(() => setReady(true));
  }, []);

  if (!ready) {
    return (
      <ThemeProvider storageKey="edupra-admin-theme">
        <div
          className="flex h-screen items-center justify-center"
          style={{ background: 'var(--bg-canvas)' }}
        >
          <div
            className="h-7 w-7 animate-spin rounded-full border-2 border-transparent"
            style={{
              borderTopColor: 'var(--color-accent-500)',
              borderRightColor: 'var(--color-accent-500)',
            }}
          />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider storageKey="edupra-admin-theme">
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<LoginPage />} />

              <Route element={<ProtectedRoute />}>
                <Route element={<AdminLayout />}>
                  <Route path="/overview" element={<OverviewPage />} />
                  <Route
                    path="/tenants"
                    element={<PlaceholderPage title="Tenants" hint="Agent 5 ships the list, detail, and provision screens." />}
                  />
                  <Route
                    path="/operators"
                    element={<PlaceholderPage title="Operators" hint="Agent 7 ships operator management." />}
                  />
                  <Route
                    path="/audit"
                    element={<PlaceholderPage title="Audit Log" hint="Agent 7 ships the audit feed." />}
                  />
                  <Route
                    path="/impersonation"
                    element={<PlaceholderPage title="Impersonation Sessions" hint="Agent 7 ships impersonation management." />}
                  />
                  <Route
                    path="/feature-flags"
                    element={<PlaceholderPage title="Feature Flags" hint="Coming soon." />}
                  />
                  <Route
                    path="/billing"
                    element={<PlaceholderPage title="Billing" hint="Coming soon." />}
                  />
                  <Route
                    path="/settings"
                    element={<PlaceholderPage title="Settings" hint="Coming soon." />}
                  />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </ThemeProvider>
  );
}

function RootRoute() {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? '/overview' : '/login'} replace />;
}
